// HTLC Secret Management Utilities
// Stores and retrieves HTLC secrets in localStorage

const SECRET_PREFIX = 'htlc-secret-';
const ORDER_SECRET_PREFIX = 'htlc-order-secret-';

/**
 * Store HTLC secret in localStorage
 */
export function storeHTLCSecret(htlcId: bigint | number, secret: Uint8Array): void {
  try {
    const key = `${SECRET_PREFIX}${htlcId}`;
    // Convert Uint8Array to base64 for storage
    const base64 = btoa(String.fromCharCode(...secret));
    localStorage.setItem(key, base64);
  } catch (error) {
    console.error('Failed to store HTLC secret:', error);
  }
}

/**
 * Retrieve HTLC secret from localStorage
 */
export function getHTLCSecret(htlcId: bigint | number): Uint8Array | null {
  try {
    const key = `${SECRET_PREFIX}${htlcId}`;
    const base64 = localStorage.getItem(key);
    if (!base64) return null;
    
    // Convert base64 back to Uint8Array
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  } catch (error) {
    console.error('Failed to retrieve HTLC secret:', error);
    return null;
  }
}

/**
 * Store order secret in localStorage (by orderId)
 */
export function storeOrderSecret(orderId: bigint | number, secret: Uint8Array): void {
  try {
    const key = `${ORDER_SECRET_PREFIX}${orderId}`;
    const base64 = btoa(String.fromCharCode(...secret));
    localStorage.setItem(key, base64);
  } catch (error) {
    console.error('Failed to store order secret:', error);
  }
}

/**
 * Retrieve order secret from localStorage
 */
export function getOrderSecret(orderId: bigint | number): Uint8Array | null {
  try {
    const key = `${ORDER_SECRET_PREFIX}${orderId}`;
    const base64 = localStorage.getItem(key);
    if (!base64) return null;
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  } catch (error) {
    console.error('Failed to retrieve order secret:', error);
    return null;
  }
}

/**
 * Generate a random secret (32 bytes)
 */
export function generateSecret(): Uint8Array {
  const secret = new Uint8Array(32);
  crypto.getRandomValues(secret);
  return secret;
}

/**
 * Generate secret hash (SHA-256)
 */
export async function generateSecretHash(secret: Uint8Array): Promise<Uint8Array> {
  const hashBuffer = await crypto.subtle.digest('SHA-256', secret);
  return new Uint8Array(hashBuffer);
}

/**
 * Verify secret matches hash
 */
export async function verifySecret(secret: Uint8Array, hash: Uint8Array): Promise<boolean> {
  const computedHash = await generateSecretHash(secret);
  if (computedHash.length !== hash.length) return false;
  
  for (let i = 0; i < computedHash.length; i++) {
    if (computedHash[i] !== hash[i]) return false;
  }
  return true;
}

/**
 * Clear HTLC secret from localStorage
 */
export function clearHTLCSecret(htlcId: bigint | number): void {
  try {
    const key = `${SECRET_PREFIX}${htlcId}`;
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Failed to clear HTLC secret:', error);
  }
}

/**
 * Clear stored order secret
 */
export function clearOrderSecret(orderId: bigint | number): void {
  try {
    const key = `${ORDER_SECRET_PREFIX}${orderId}`;
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Failed to clear order secret:', error);
  }
}

/**
 * Clear all HTLC secrets (cleanup)
 */
export function clearAllHTLCSecrets(): void {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(SECRET_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error('Failed to clear HTLC secrets:', error);
  }
}







